import { EntityTable } from '~/store/slices/dipsSlice'
import { useStore } from '~/store/store'
import { UnifiedScheduledSurgery } from '~/store/transformers/scheduledSurgeryTransformer'
import { UnifiedUnScheduledSurgery } from '~/store/transformers/unScheduledSurgery/unScheduledSurgeryTransformer'

export function getDepartmentNameAndSurgeries(surgeries: EntityTable<string, UnifiedScheduledSurgery | UnifiedUnScheduledSurgery>) {
    const departmentKey = useStore.getState().appFilters.departmentKey

    if (departmentKey === 'all') {
        return {
            departmentName: 'Alle',
            surgeries,
        }
    }

    const departmentsById = useStore.getState().di.entities.departments.byId
    const sections = useStore.getState().di.entities.sections

    const selectedDepartment = departmentsById[departmentKey]
    const selectedSections = sections.allIds.map(sectionId => sections.byId[sectionId]).filter(section => section?.department_id === departmentKey)

    const filteredSurgeryIds = surgeries.allIds.filter(surgeryId => {
        const surgerySectionId = surgeries.byId[surgeryId]?.contact?.section?.id
        return surgerySectionId !== undefined && selectedSections.some(section => section?.dips_section_id === surgerySectionId)
    })

    return {
        departmentName: selectedDepartment?.name ?? 'Ukjent',
        surgeries: {
            ...surgeries,
            allIds: filteredSurgeryIds,
        },
    }
}

type OrgWithCount = { shortName: string | null | undefined; name: string | null | undefined; count: number }

// Temporary logging for configuring correct DIPS departments
export function getUniqueDIPSOrgs(surgeries: EntityTable<string, UnifiedScheduledSurgery | UnifiedUnScheduledSurgery>) {
    const totalItems = surgeries.allIds.length

    const counters = {
        missingSurgeryIdItems: 0,
        missingSurgeryItems: 0,
    }

    const uniqueDepartmentsMap = new Map<number | undefined, OrgWithCount>()
    const uniqueWardsMap = new Map<number | undefined, OrgWithCount>()
    const uniqueLocalizationsMap = new Map<number | undefined, OrgWithCount>()

    for (let i = 0; i < totalItems; i++) {
        const surgeryId = surgeries.allIds[i]
        if (!surgeryId) {
            counters.missingSurgeryIdItems++
            continue
        }

        const surgery = surgeries.byId[surgeryId]
        if (!surgery) {
            counters.missingSurgeryItems++
            continue
        }

        const department = surgery.contact?.department
        const existingDepartment = uniqueDepartmentsMap.get(department?.id)

        if (existingDepartment) {
            existingDepartment.count += 1
        } else {
            uniqueDepartmentsMap.set(department?.id, { shortName: department?.shortName, name: department?.name, count: 1 })
        }

        const ward = surgery.contact?.ward
        const existingWard = uniqueWardsMap.get(ward?.id)

        if (existingWard) {
            existingWard.count += 1
        } else {
            uniqueWardsMap.set(ward?.id, { shortName: ward?.shortName, name: ward?.name, count: 1 })
        }

        const localization = surgery.contact?.location
        const existingLocalization = uniqueLocalizationsMap.get(localization?.id)

        if (existingLocalization) {
            existingLocalization.count += 1
        } else {
            uniqueLocalizationsMap.set(localization?.id, { shortName: localization?.shortName, name: localization?.name, count: 1 })
        }
    }

    return {
        totalItems: totalItems,
        ...counters,
        uniqueDepartments: Object.fromEntries(uniqueDepartmentsMap),
        uniqueWards: Object.fromEntries(uniqueWardsMap),
        uniqueLocalizations: Object.fromEntries(uniqueLocalizationsMap),
    }
}
